import { Controller } from "@hotwired/stimulus"
import { TRACKING_CONSENT_ACCEPTED_EVENT, track, trackingAllowed } from "../lib/analytics"

// Fires page_view once per page load (and once per Turbo navigation, since
// Stimulus controllers reconnect on Turbo navigations to the body), plus
// a page_load_metrics event after the page settles so the analytics
// dashboard can show "Avg page load" without needing a separate
// performance pipeline. Page-load measurements come from the standard
// PerformanceNavigationTiming + Paint APIs — no third-party RUM library.
export default class extends Controller {
  connect() {
    this.onTrackingConsentAccepted = this.onTrackingConsentAccepted.bind(this)
    document.addEventListener(TRACKING_CONSENT_ACCEPTED_EVENT, this.onTrackingConsentAccepted)
    if (trackingAllowed()) this.fire()
  }

  disconnect() {
    document.removeEventListener(TRACKING_CONSENT_ACCEPTED_EVENT, this.onTrackingConsentAccepted)
  }

  onTrackingConsentAccepted() {
    this.fire()
  }

  fire() {
    // Guard against the rare double-connect Stimulus may issue on the same
    // element instance.
    if (this._fired) return
    this._fired = true
    track("page_view")
    this._reportPageLoad()
  }

  // Fires page_load_metrics with load_ms / DCL / FCP. Waits for the
  // window load event because navigation timings are only finalized
  // after the load handler runs. The extra `setTimeout(0)` defers one
  // more tick so loadEventEnd is populated (it's set immediately AFTER
  // the load handler returns — reading it inside the handler itself
  // yields 0).
  _reportPageLoad() {
    const fire = () => setTimeout(() => this._sendLoadMetrics(), 0)
    if (document.readyState === "complete") {
      fire()
    } else {
      window.addEventListener("load", fire, { once: true })
    }
  }

  _sendLoadMetrics() {
    try {
      const nav = performance.getEntriesByType("navigation")[0]
      if (!nav) return

      const loadMs = Math.max(0, Math.round(nav.loadEventEnd))
      if (loadMs === 0) return // load not yet finalized

      const dclMs = Math.max(0, Math.round(nav.domContentLoadedEventEnd))
      const fcpEntry = performance.getEntriesByType("paint")
        .find(e => e.name === "first-contentful-paint")
      const fcpMs = fcpEntry ? Math.max(0, Math.round(fcpEntry.startTime)) : null

      const props = { load_ms: loadMs }
      if (dclMs > 0) props.dom_content_loaded_ms = dclMs
      if (fcpMs !== null) props.first_contentful_paint_ms = fcpMs

      track("page_load_metrics", props)
    } catch (_) { /* silent — performance API missing or restricted */ }
  }
}
